import React from 'react';
import { type VariantProps } from 'class-variance-authority';
import Slot from '@/components/ui/Slot';
import cn from '@/utils/cn';
import {
  breakpointVariants,
  defaultTagForVariant,
  FontWeight,
  spacingVariants,
  textVariants,
  weightVariants,
} from '@/components/ui/Text/Text.utils';

type TextTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'div' | 'span' | 'label';

interface StyleProps {
  italic?: boolean;
  size?: 'lg' | 'sm' | 'xl' | 'xs';
  spacing?: 'lg' | 'sm';
  weight?: FontWeight;
}

type ResponsiveStyles = {
  size?: 'lg' | 'md' | 'sm' | 'xl' | 'xs';
};

interface OwnProps<T extends TextTag> extends VariantProps<typeof textVariants>, StyleProps {
  asChild?: boolean;
  children?: React.ReactNode;
  className?: string;
  tag?: T;
  sm?: ResponsiveStyles;
  md?: ResponsiveStyles;
  lg?: ResponsiveStyles;
  xl?: ResponsiveStyles;
  xxl?: ResponsiveStyles;
}

// AIDEV-NOTE: Listed explicitly (rather than `keyof OwnProps<T>`) so TS can resolve `Omit` while
// `T` is still generic — otherwise the responsive props (`md`/`lg`/etc.) leak through and collide.
type OwnPropKey =
  | 'asChild' | 'children' | 'className' | 'tag'
  | 'sm' | 'md' | 'lg' | 'xl' | 'xxl'
  | 'italic' | 'size' | 'spacing' | 'variant' | 'weight';

// AIDEV-NOTE: Generic on `tag` so consumers get tag-aware HTML props (e.g. `htmlFor` on `<label>`,
// `id`/`role`/`aria-*`/event handlers everywhere). Defaults to `'p'` to match the `'body'` variant.
type TextProps<T extends TextTag = 'p'> =
  Prettify<OwnProps<T> & Omit<React.ComponentPropsWithoutRef<T>, OwnPropKey>>;

const Text = <T extends TextTag = 'p'>(props: TextProps<T>) => {
  // AIDEV-NOTE: Cast through `OwnProps<TextTag>` so destructuring resolves the responsive-prop
  // types concretely; `rest` is forwarded via spread so we don't need its individual prop types.
  const {
    asChild,
    children,
    className,
    italic,
    md = {},
    lg = {},
    xl = {},
    xxl = {},
    size,
    sm = {},
    spacing,
    tag,
    variant,
    weight,
    ...rest
  } = props as OwnProps<TextTag> & Record<string, unknown>;
  const Component = (asChild ? Slot : (tag || defaultTagForVariant(variant))) as React.ElementType;

  return (
    <Component
      {...rest}
      className={cn(
        textVariants({ italic, size, variant }),
        spacingVariants({ spacing }),
        weightVariants({ weight }),
        className,
        {
          [breakpointVariants.sm({ size: sm.size })]: Object.keys(sm).length > 0,
          [breakpointVariants.md({ size: md.size })]: Object.keys(md).length > 0,
          [breakpointVariants.lg({ size: lg.size })]: Object.keys(lg).length > 0,
          [breakpointVariants.xl({ size: xl.size })]: Object.keys(xl).length > 0,
          [breakpointVariants.xxl({ size: xxl.size })]: Object.keys(xxl).length > 0,
        },
      )}
    >
      {children}
    </Component>
  );
};

export default Text;
